/**
 * Iter 86.4 / Phase E6 Sprint 2A — Desktop Agent · ESS Self-View.
 *
 * Lets an employee see their own desktop-agent registration + today's
 * activity + a 14-day daily-summary trend. Read-only.
 *
 * Iter 89.3 / Sprint 2A.2 — adds the day-picker + 288-slot timeline
 * strip below the existing "Today" tile. The timeline section calls
 * /api/workforce/desktop-activity/me/timeline which is flag-gated
 * (WORKFORCE_TIMELINE_ENABLED). When the flag is OFF the endpoint
 * returns 404 and we silently hide the timeline section, leaving the
 * existing ESS surfaces unchanged.
 *
 * Governing principle: presence-monitoring, not surveillance.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ArrowsClockwise, CircleNotch, Info, ClockClockwise, Desktop,
    CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import ActivityTimelineStrip from "../../components/workforce/ActivityTimelineStrip";

const OP_BADGE = {
    active: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500", label: "Active" },
    idle:   { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-800",   dot: "bg-amber-500",   label: "Idle" },
    offline:{ bg: "bg-slate-50",   border: "border-slate-300",   text: "text-slate-700",   dot: "bg-slate-400",   label: "Offline" },
};

function humanAgo(iso) {
    if (!iso) return "—";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return "—";
    const diffSec = Math.round((Date.now() - ts) / 1000);
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    const m = Math.round(diffSec / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ${m % 60}m ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
}

function fmtSecs(s) {
    const total = Number(s) || 0;
    if (total === 0) return "0m";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

// Iter 89.3 / Sprint 2A.2 — day-picker helpers.
// LOCAL-CALENDAR-DAY semantics. The backend's `/me/timeline` endpoint
// applies the WORKFORCE_LOCAL_TIMEZONE env (default Asia/Kolkata) so
// we keep the wire format YYYY-MM-DD in the SAME local calendar day
// the operator typed in. No timezone conversion on the client side.
const MAX_DAYS_BACK = 30;   // bounded historical view per Sprint 2A scope

function todayLocalDateString() {
    // Returns YYYY-MM-DD in the browser's local timezone. The backend
    // resolves the same date in WORKFORCE_LOCAL_TIMEZONE — small skew
    // possible if the operator browses across midnight; acceptable for
    // self-view UX.
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function addDaysToISO(iso, deltaDays) {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    const yyyy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function deriveDayPickerMeta(selectedISO) {
    const todayISO = todayLocalDateString();
    const earliestISO = addDaysToISO(todayISO, -MAX_DAYS_BACK);
    return {
        todayISO,
        earliestISO,
        canGoPrev: selectedISO > earliestISO,
        canGoNext: selectedISO < todayISO,
        isToday: selectedISO === todayISO,
    };
}

export default function EssMyDesktopActivity({ embedded = false }) {
    const [me, setMe] = useState(null);
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(false);

    // Iter 89.3 — Timeline state. `enabled` is false until the first
    // 200 response from /me/timeline; a 404 (flag off) keeps it false
    // and the timeline section stays hidden — no error toast, no
    // visible UI churn for ops that haven't flipped the flag yet.
    const [timelineEnabled, setTimelineEnabled] = useState(false);
    const [timelineDate, setTimelineDate] = useState(todayLocalDateString);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineData, setTimelineData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [meResp, sumResp] = await Promise.all([
                api.get("/workforce/desktop-activity/me"),
                api.get("/workforce/desktop-activity/me/summary?days=14"),
            ]);
            setMe(meResp.data);
            setSeries(sumResp.data?.series || []);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                // User has no employee link — show empty state, not an error toast.
                setMe(null);
                setSeries([]);
            } else {
                toast.error(formatApiError(err, "Failed to load my activity"));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTimeline = useCallback(async (date) => {
        setTimelineLoading(true);
        try {
            const resp = await api.get(
                `/workforce/desktop-activity/me/timeline?date=${encodeURIComponent(date)}`,
            );
            setTimelineData(resp.data);
            setTimelineEnabled(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                // Flag OFF on the server OR user has no employee link. Either
                // way, hide the section silently. Existing surfaces (Today /
                // 14-day table) keep working.
                setTimelineEnabled(false);
                setTimelineData(null);
            } else {
                toast.error(formatApiError(err, "Failed to load timeline"));
            }
        } finally {
            setTimelineLoading(false);
        }
    }, []);

    useEffect(() => {
        // Defer initial reload via post-commit microtask to satisfy
        // React 19 react-hooks/set-state-in-effect.
        Promise.resolve().then(() => { load(); });
    }, [load]);

    useEffect(() => {
        Promise.resolve().then(() => { loadTimeline(timelineDate); });
    }, [loadTimeline, timelineDate]);

    const opStatus = me?.operational_status || "offline";
    const opTone = OP_BADGE[opStatus] || OP_BADGE.offline;
    const reg = me?.registration;

    const dayMeta = useMemo(() => deriveDayPickerMeta(timelineDate), [timelineDate]);
    const refresh = (
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="ess-refresh-btn">
            {loading ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
            Refresh
        </Button>
    );

    return (
        <div data-testid="ess-my-desktop-activity">
            {!embedded && <PageHeader eyebrow="My Workspace" title="My Desktop Activity" actions={refresh} />}

            <div className={embedded ? "pt-4 space-y-6" : "px-6 sm:px-8 py-6 space-y-6 max-w-4xl"}>
                {embedded && (
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-xs text-slate-500">Your desktop-agent registration and presence record. Read-only. You can pause the agent any time from the tray icon on your device.</div>
                        {refresh}
                    </div>
                )}

                <div
                    className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                    data-testid="presence-not-surveillance-banner"
                >
                    <Info size={20} weight="fill" className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 text-xs text-blue-900 leading-relaxed">
                        <span className="font-semibold">Presence-monitoring tool, not a surveillance tool.</span>
                        {" "}This is what your manager sees about your device. Nothing more, nothing less — no screen content, no keystrokes, no clipboard, no media, no URLs, no apps, no files.
                    </div>
                </div>

                {!reg ? (
                    <div className="rounded-lg border border-slate-200 bg-white px-6 py-10 text-center">
                        <Desktop size={28} className="text-slate-300 mx-auto mb-2" />
                        <div className="font-semibold text-slate-700">No desktop agent is registered for you yet.</div>
                        <div className="text-sm text-slate-500 mt-1">When an admin issues a registration code and you install the agent on your device, this page will show your activity.</div>
                    </div>
                ) : (
                    <>
                        {/* Status card */}
                        <div className="rounded-lg border border-slate-200 bg-white p-6">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Current Status</div>
                                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${opTone.border} ${opTone.bg} ${opTone.text} text-sm font-semibold`} data-testid="ess-op-status">
                                        <span className={`inline-block h-2 w-2 rounded-full ${opTone.dot}`} />
                                        {opTone.label}
                                    </span>
                                    <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                                        <ClockClockwise size={11} /> Last seen {humanAgo(reg.last_handshake_at)}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Machine</div>
                                    <div className="font-mono text-sm text-slate-800" data-testid="ess-machine-name">{reg.machine_name || "—"}</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">Agent v{reg.agent_version || "?"}</div>
                                </div>
                            </div>
                        </div>

                        {/* Today */}
                        <div className="rounded-lg border border-slate-200 bg-white p-6">
                            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-3">Today</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div data-testid="ess-today-active">
                                    <div className="text-[11px] text-slate-500">Active</div>
                                    <div className="text-2xl font-bold tabular-nums text-emerald-700">{fmtSecs(me?.today?.active_seconds)}</div>
                                </div>
                                <div data-testid="ess-today-idle">
                                    <div className="text-[11px] text-slate-500">Idle</div>
                                    <div className="text-2xl font-bold tabular-nums text-amber-700">{fmtSecs(me?.today?.idle_seconds)}</div>
                                </div>
                                <div data-testid="ess-today-locked">
                                    <div className="text-[11px] text-slate-500">Locked</div>
                                    <div className="text-2xl font-bold tabular-nums text-slate-700">{fmtSecs(me?.today?.locked_seconds)}</div>
                                </div>
                                <div data-testid="ess-today-windows">
                                    <div className="text-[11px] text-slate-500">Windows reported</div>
                                    <div className="text-2xl font-bold tabular-nums text-slate-700">{me?.today?.windows_reported || 0}</div>
                                </div>
                            </div>
                        </div>

                        {/* Iter 89.3 / Sprint 2A.2 — Timeline strip (flag-gated). */}
                        {/* Section is rendered only when the backend's flag-gated */}
                        {/* /me/timeline endpoint returns 200 — keeping the surface */}
                        {/* invisible to operators who haven't flipped the flag.    */}
                        {timelineEnabled && (
                            <div
                                className="rounded-lg border border-slate-200 bg-white p-6"
                                data-testid="ess-timeline-section"
                            >
                                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Daily timeline</div>
                                        <div className="text-sm text-slate-700 font-medium" data-testid="ess-timeline-date">
                                            {timelineDate}{dayMeta.isToday ? " · today" : ""}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!dayMeta.canGoPrev || timelineLoading}
                                            onClick={() => setTimelineDate((d) => addDaysToISO(d, -1))}
                                            className="gap-1.5"
                                            data-testid="ess-timeline-prev-day"
                                            aria-label="Previous day"
                                        >
                                            <CaretLeft size={14} />
                                            Previous
                                        </Button>
                                        <input
                                            type="date"
                                            value={timelineDate}
                                            min={dayMeta.earliestISO}
                                            max={dayMeta.todayISO}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                if (next) setTimelineDate(next);
                                            }}
                                            className="h-8 px-2 rounded border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                            data-testid="ess-timeline-date-picker"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!dayMeta.canGoNext || timelineLoading}
                                            onClick={() => setTimelineDate((d) => addDaysToISO(d, 1))}
                                            className="gap-1.5"
                                            data-testid="ess-timeline-next-day"
                                            aria-label="Next day"
                                        >
                                            Next
                                            <CaretRight size={14} />
                                        </Button>
                                    </div>
                                </div>
                                {timelineLoading ? (
                                    <div className="h-9 flex items-center justify-center text-slate-400 text-xs">
                                        <CircleNotch size={14} className="animate-spin mr-2" /> Loading timeline…
                                    </div>
                                ) : timelineData ? (
                                    <>
                                        <ActivityTimelineStrip
                                            timeline={timelineData.timeline || []}
                                            slotMinutes={timelineData.slot_minutes || 5}
                                            slotsPerDay={timelineData.slots_per_day || 288}
                                            testIdPrefix="ess-timeline"
                                        />
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
                                            <div data-testid="ess-timeline-summary-active">
                                                <div className="text-[11px] text-slate-500">Active</div>
                                                <div className="font-mono text-emerald-700">{fmtSecs(timelineData.summary?.active_seconds)}</div>
                                            </div>
                                            <div data-testid="ess-timeline-summary-idle">
                                                <div className="text-[11px] text-slate-500">Idle</div>
                                                <div className="font-mono text-amber-700">{fmtSecs(timelineData.summary?.idle_seconds)}</div>
                                            </div>
                                            <div data-testid="ess-timeline-summary-locked">
                                                <div className="text-[11px] text-slate-500">Locked</div>
                                                <div className="font-mono text-slate-700">{fmtSecs(timelineData.summary?.locked_seconds)}</div>
                                            </div>
                                            <div data-testid="ess-timeline-summary-windows">
                                                <div className="text-[11px] text-slate-500">Windows</div>
                                                <div className="font-mono text-slate-700">{timelineData.summary?.windows_reported ?? 0}</div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-slate-400 text-xs">No timeline data.</div>
                                )}
                            </div>
                        )}

                        {/* Last 14 days */}
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-3 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">Last 14 days</div>
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-semibold">Date</th>
                                        <th className="text-right px-4 py-2 font-semibold">Active</th>
                                        <th className="text-right px-4 py-2 font-semibold">Idle</th>
                                        <th className="text-right px-4 py-2 font-semibold">Locked</th>
                                        <th className="text-right px-4 py-2 font-semibold">Windows</th>
                                        <th className="text-right px-4 py-2 font-semibold">Consistency</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {series.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-sm">No data yet.</td>
                                        </tr>
                                    ) : series.map((d) => (
                                        <tr key={d.date} data-testid={`ess-series-${d.date}`} className="hover:bg-slate-50">
                                            <td className="px-4 py-2 text-slate-700">{d.date}</td>
                                            <td className="px-4 py-2 text-right font-mono text-emerald-700">{fmtSecs(d.active_seconds)}</td>
                                            <td className="px-4 py-2 text-right font-mono text-amber-700">{fmtSecs(d.idle_seconds)}</td>
                                            <td className="px-4 py-2 text-right font-mono text-slate-700">{fmtSecs(d.locked_seconds)}</td>
                                            <td className="px-4 py-2 text-right font-mono text-slate-600">{d.windows_reported}</td>
                                            <td className="px-4 py-2 text-right font-mono text-slate-600">{(d.presence_consistency_pct ?? 0).toFixed(1)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
